import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'designSystem/lib/utils'
import * as React from 'react'

const toolbarVariants = cva(
  'flex w-full shrink-0 items-center text-foreground',
  {
    variants: {
      size: {
        default: 'h-9 gap-1.5 px-3',
        lg: 'h-12 gap-3 px-3',
      },
      border: {
        none: '',
        bottom: 'border-b border-border',
      },
      wrap: {
        false: '',
        true: 'h-auto min-h-9 flex-wrap py-1',
      },
    },
    compoundVariants: [{ size: 'lg', wrap: true, class: 'min-h-12' }],
    defaultVariants: {
      size: 'default',
      border: 'none',
      wrap: false,
    },
  }
)

/** Horizontal app/page toolbar: compose with ToolbarSection / ToolbarSpacer. */
function Toolbar({
  className,
  size,
  border,
  wrap,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof toolbarVariants>) {
  return (
    <div
      data-slot='toolbar'
      className={cn(toolbarVariants({ size, border, wrap, className }))}
      {...props}
    />
  )
}

function ToolbarSection({
  grow = false,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  /** Take the remaining row width, shrinking from a zero basis. */
  grow?: boolean
}) {
  return (
    <div
      data-slot='toolbar-section'
      className={cn(
        'flex min-w-0 items-center gap-1',
        grow && 'flex-1 basis-0',
        className
      )}
      {...props}
    />
  )
}

function ToolbarSpacer({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='toolbar-spacer'
      className={cn('flex-1', className)}
      {...props}
    />
  )
}

function ToolbarSeparator({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='toolbar-separator'
      className={cn('mx-1 h-4 w-px shrink-0 bg-border', className)}
      {...props}
    />
  )
}

function ToolbarTitle({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot='toolbar-title'
      className={cn('truncate text-sm font-medium', className)}
      {...props}
    />
  )
}

export {
  Toolbar,
  ToolbarSection,
  ToolbarSeparator,
  ToolbarSpacer,
  ToolbarTitle,
}
